// validationSchemas.js
import * as Yup from "yup";

/**
 * Build a Yup schema for signed change documents.
 *
 * - Requires a File for each doc in `documentData` (preferred), OR
 * - Requires a truthy value (string key) if you’re storing uploaded keys instead of File objects.
 *
 * Usage:
 *   validationSchema={getSignedUploadValidationSchema(documentData)}
 */
export const getSignedUploadValidationSchema = (documentData = []) => {
  const fileUploadsShape = (documentData || []).reduce((acc, doc) => {
    const { name, label } = doc;

    acc[name] = Yup.mixed()
      .required(`${label || "This document"} is required`)
      .test(
        "file-or-key",
        `${label || "This document"} is required`,
        (value) => {
          // Accept either:
          // 1) a File object chosen by the user
          // 2) a string key returned from upload (if you persist keys in form state)
          if (!value) return false;
          if (typeof value === "string") return value.trim().length > 0;
          if (typeof File !== "undefined" && value instanceof File) return true;
          // some upload components pass { file: File } or similar
          if (
            value?.file &&
            typeof File !== "undefined" &&
            value.file instanceof File
          )
            return true;

          return true; // allow other truthy shapes if your uploader uses custom objects
        }
      )
      .test(
        "file-type",
        `Only PDF or DOCX is allowed for ${label || "this document"}`,
        (value) => {
          // If it’s a persisted key, we can’t reliably infer mime here—skip.
          if (!value || typeof value === "string") return true;

          const file = value instanceof File ? value : value?.file;
          if (!file) return true;

          const allowedMimes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ];

          // Some browsers/components may omit type; in that case, fall back to extension
          if (!file.type) {
            const n = (file.name || "").toLowerCase();
            return n.endsWith(".pdf") || n.endsWith(".docx");
          }

          return allowedMimes.includes(file.type);
        }
      );

    return acc;
  }, {});

  return Yup.object().shape({
    fileUploads: Yup.object().shape(fileUploadsShape),
  });
};

/**
 * If you want a single exported schema (no builder),
 * pass the relevant documentData in your component file and use the builder above.
 *
 * Example:
 *   const validationSchema = useMemo(
 *     () => getSignedUploadValidationSchema(documentData),
 *     [documentData]
 *   );
 */

// If you still want a named export that matches your import:
// (keep this only if you can’t change the import path/usage)
export const uploadChangeDocumentValidationSchema = (documentData = []) =>
  getSignedUploadValidationSchema(documentData);
